"use client";

import { useEffect, useRef } from "react";
import type { ReportData } from "./reportData";
import type { BirthFormData } from "./BirthDataForm";
import AvatarSnapshotCard from "./AvatarSnapshotCard";
import AvatarEssenceSection from "./AvatarEssenceSection";
import AvatarBlueprintSection from "./AvatarBlueprintSection";
import AlignmentPlanSection from "./AlignmentPlanSection";
import AvatarStatementSection from "./AvatarStatementSection";
import ReflectionSection from "./ReflectionSection";
import ReportActions from "./ReportActions";

interface ReportDisplayProps {
  report: ReportData;
  formData: BirthFormData;
  onReset: () => void;
}

function formatBirthDate(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatBirthTime(timeStr: string): string {
  if (!timeStr) return "Time unknown";
  const [hours, minutes] = timeStr.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
}

const WEEK_LABELS = ["Week 1", "Week 2", "Week 3", "Week 4"];

export default function ReportDisplay({ report, formData, onReset }: ReportDisplayProps) {
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, []);

  return (
    <div className="w-full max-w-screen-xl mx-auto" ref={topRef}>

      {/* ─── PRINT-ONLY LAYOUT ─────────────────────────────────────────────────
          Invisible on screen. Only rendered when printing.
          No card wrappers, no borders, no backgrounds — just text and icons.
      ──────────────────────────────────────────────────────────────────────── */}
      <div className="print-only-layout" aria-hidden="true">

        {/* Cover */}
        <div className="po-cover">
          <p className="po-eyebrow">QuantuMe Nautilus · Avatar Blueprint</p>
          <h1 className="po-name">{formData.name}</h1>
          <p className="po-meta">
            {formatBirthDate(formData.birthdate)}
            {formData.birthtime ? ` · ${formatBirthTime(formData.birthtime)}` : ""}
            {formData.birthplace ? ` · ${formData.birthplace}` : ""}
          </p>
          <p className="po-archetype">✦ {report.snapshot.primaryArchetype}</p>
        </div>

        {/* Snapshot */}
        <div className="po-section">
          <h2 className="po-section-title">★ Avatar Snapshot</h2>
          <p className="po-label">Core Theme</p>
          <p className="po-body">"{report.snapshot.coreTheme}"</p>
          <div className="po-grid-3">
            <div>
              <p className="po-label">⚡ Key Strength</p>
              <p className="po-body">{report.snapshot.keyStrength}</p>
            </div>
            <div>
              <p className="po-label">↑ Growth Edge</p>
              <p className="po-body">{report.snapshot.growthEdge}</p>
            </div>
            <div>
              <p className="po-label">◎ Life Direction</p>
              <p className="po-body">{report.snapshot.lifeDirection}</p>
            </div>
          </div>
        </div>

        {/* Essence */}
        <div className="po-section">
          <h2 className="po-section-title">1 · Your Avatar Essence</h2>

          <p className="po-label">Archetype Overview</p>
          <p className="po-body">{report.essence.archetypeOverview}</p>

          <p className="po-label">Core Traits</p>
          <p className="po-body">{report.essence.coreTraits.join(" · ")}</p>

          <p className="po-label">Natural Strengths</p>
          <ul className="po-list">
            {report.essence.strengths.map((s, i) => (
              <li key={i}>• {s}</li>
            ))}
          </ul>

          <p className="po-label">Growth Challenges</p>
          <ul className="po-list">
            {report.essence.growthChallenges.map((c, i) => (
              <li key={i}>• {c}</li>
            ))}
          </ul>
        </div>

        {/* Blueprint */}
        <div className="po-section">
          <h2 className="po-section-title">2 · Avatar Blueprint</h2>

          <p className="po-label">Personality Analysis</p>
          <p className="po-body">{report.blueprint.personalityAnalysis}</p>

          <p className="po-label">Life Path Insights</p>
          <p className="po-body">{report.blueprint.lifePathInsights}</p>

          <p className="po-label">Cosmic Influences</p>
          <p className="po-body">{report.blueprint.cosmicInfluences}</p>

          <p className="po-label">Related Archetypes</p>
          <ul className="po-list">
            {report.blueprint.relatedArchetypes.map((a, i) => (
              <li key={i}><strong>{a.name}</strong> — {a.description}</li>
            ))}
          </ul>
        </div>

        {/* Alignment Plan */}
        <div className="po-section">
          <h2 className="po-section-title">3 · 30-Day Alignment Plan</h2>
          <p className="po-body po-italic">
            This plan is designed around your archetypal nature. Each week builds on the last, moving you from awareness to full embodiment.
          </p>
          {report.alignmentPlan.weeks.map((week, wi) => (
            <div key={wi} className="po-week">
              <p className="po-week-label">{WEEK_LABELS[wi] || `Week ${wi + 1}`} — {week.theme}</p>
              <ul className="po-list">
                {week.actions.map((action, ai) => (
                  <li key={ai}>{ai + 1}. {action}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Avatar Statement */}
        <div className="po-section">
          <h2 className="po-section-title">4 · Your Avatar Statement</h2>
          <p className="po-name-small">{formData.name}</p>
          <blockquote className="po-quote">{report.avatarStatement}</blockquote>

          <p className="po-label">A Final Word for You</p>
          <p className="po-body">{report.finalMessage}</p>
        </div>

        {/* Reflection */}
        <div className="po-section">
          <h2 className="po-section-title">5 · Reflection &amp; Integration</h2>
          <p className="po-body">
            Your Avatar Blueprint is more than a report — it is an invitation. An invitation to see yourself more clearly, to act with greater intention, and to step more fully into the life that is uniquely yours.
          </p>

          <p className="po-label">Three Pillars</p>
          <ul className="po-list">
            <li>🌱 <strong>Sit with It</strong> — Read your Blueprint slowly. Let the insights settle. Some truths need time to take root before they can grow.</li>
            <li>📓 <strong>Journal Your Responses</strong> — Write down what resonates most strongly. Your reactions are data.</li>
            <li>🧭 <strong>Return Often</strong> — Your Blueprint doesn't expire. Return to it at crossroads or whenever you feel unclear about your direction.</li>
          </ul>

          <p className="po-label">Questions to Carry Forward</p>
          <ul className="po-list">
            <li>01. Where in my life am I most fully myself — and where am I holding back?</li>
            <li>02. What would change if I fully trusted the strengths described in my Blueprint?</li>
            <li>03. Which growth challenge is asking for my attention right now?</li>
            <li>04. What does my 30-Day Plan reveal about what I've been avoiding?</li>
            <li>05. If I embodied my Avatar Statement completely, what would my life look like in one year?</li>
          </ul>

          <p className="po-closing-quote">"The greatest journey you will ever take is the one that leads you back to yourself."</p>
          <p className="po-footer">QuantuMe Nautilus · Avatar Blueprint System · March 2026</p>
        </div>
      </div>

      {/* ─── SCREEN LAYOUT (hidden when printing) ──────────────────────────── */}
      <div className="screen-only-layout">
        {/* Report Header */}
        <div className="text-center mb-10 fade-in">
          <div className="inline-flex items-center gap-2 mb-5">
            <div
              className="h-px w-16"
              style={{ background: "linear-gradient(90deg, transparent, rgba(219,184,77,0.5))" }}
            />
            <span
              className="text-xs font-600 tracking-widest uppercase"
              style={{ color: "rgba(219,184,77,0.6)", letterSpacing: "0.14em", fontWeight: 600 }}
            >
              Your QuantuMe Nautilus
            </span>
            <div
              className="h-px w-16"
              style={{ background: "linear-gradient(90deg, rgba(219,184,77,0.5), transparent)" }}
            />
          </div>

          <h1
            className="text-3xl md:text-4xl lg:text-5xl font-display mb-5 leading-tight"
            style={{ fontWeight: 800 }}
          >
            <span className="text-white/90">Avatar Blueprint</span>
            <br />
            <span className="gold-gradient-text">{formData.name}</span>
          </h1>

          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5 mb-6">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: "rgba(54,23,64,0.6)",
                border: "1px solid rgba(219,184,77,0.2)",
              }}
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "rgba(219,184,77,0.6)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span className="text-xs text-white/60" style={{ fontWeight: 500 }}>
                {formatBirthDate(formData.birthdate)}
              </span>
            </div>

            {formData.birthtime && (
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full"
                style={{
                  background: "rgba(54,23,64,0.6)",
                  border: "1px solid rgba(219,184,77,0.2)",
                }}
              >
                <svg className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "rgba(219,184,77,0.6)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span className="text-xs text-white/60" style={{ fontWeight: 500 }}>
                  {formatBirthTime(formData.birthtime)}
                </span>
              </div>
            )}

            <div
              className="flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: "rgba(54,23,64,0.6)",
                border: "1px solid rgba(219,184,77,0.2)",
              }}
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "rgba(219,184,77,0.6)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span className="text-xs text-white/60" style={{ fontWeight: 500 }}>
                {formData.birthplace}
              </span>
            </div>
          </div>

          <div className="inline-block archetype-badge">
            ✦ {report.snapshot.primaryArchetype}
          </div>
        </div>

        {/* Report Actions (top) */}
        <div className="mb-8">
          <ReportActions onReset={onReset} name={formData.name} report={report} />
        </div>

        {/* Report Content */}
        <div className="space-y-10">
          <div className="fade-in-up stagger-1">
            <AvatarSnapshotCard report={report} />
          </div>
          <div className="section-divider" />
          <div className="fade-in-up stagger-2">
            <AvatarEssenceSection report={report} />
          </div>
          <div className="section-divider" />
          <div className="fade-in-up stagger-3">
            <AvatarBlueprintSection report={report} />
          </div>
          <div className="section-divider" />
          <div className="fade-in-up stagger-4">
            <AlignmentPlanSection report={report} />
          </div>
          <div className="section-divider" />
          <div className="fade-in-up stagger-5">
            <AvatarStatementSection report={report} name={formData.name} />
          </div>
          <div className="section-divider" />
          <div className="fade-in-up stagger-6">
            <ReflectionSection />
          </div>
        </div>

        {/* Report Actions (bottom) */}
        <div className="mt-10">
          <ReportActions onReset={onReset} name={formData.name} report={report} />
        </div>

        {/* Bottom signature */}
        <div className="text-center mt-12 mb-4">
          <div
            className="h-px w-32 mx-auto mb-4"
            style={{ background: "linear-gradient(90deg, transparent, rgba(219,184,77,0.3), transparent)" }}
          />
          <p className="text-xs text-white/25 font-mono" style={{ fontVariantNumeric: "tabular-nums" }}>
            QuantuMe Nautilus · Avatar Blueprint System · March 2026
          </p>
        </div>
      </div>
    </div>
  );
}